import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { faChevronLeft, faRotate } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { indicate } from 'src/app/operators';
import {
  ApiException,
  EsraPipingAPIClient,
  UpdateUserBusinessUnitAccessRequest,
  UserBusinessUnitAccessViewModel,
  UserDetailsViewModel,
} from 'src/app/shared/models/autogenerated-piping';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';

/** A business unit row as rendered on the access screen, with a local checkbox state. */
interface BusinessUnitRow {
  businessUnitId: string;
  name: string;
  isMandatory: boolean;
  /** Local checkbox state, seeded from `isAssigned` and toggled by the administrator. */
  isChecked: boolean;
}

/**
 * Displays one user's read-only Azure-synchronized attributes and lets a Piping Admin manage
 * the optional business units the user has access to. Mandatory business units are always-on
 * and cannot be toggled here (see `UserBusinessUnitAccessViewModel.isMandatory`).
 *
 * Note: the API does not expose whether a currently-assigned optional business unit is still
 * active. Resending an inactive assigned business unit's id on save is rejected by the backend
 * with a 400 -- if that happens, the returned error message is shown as-is so the administrator
 * knows to uncheck the affected business unit and retry.
 */
@Component({
    selector: 'app-user-management-detail',
    templateUrl: './user-management-detail.component.html',
    styleUrls: ['./user-management-detail.component.scss'],
    standalone: false
})
export class UserManagementDetailComponent implements OnInit {
  private readonly esraApiClient = inject(EsraPipingAPIClient);
  private readonly snackBarService = inject(SnackBarService);
  private readonly insightsService = inject(InsightsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private userId: string = '';
  /** Concurrency token from the last successful GET/PUT -- resent unchanged on the next save. */
  private accessUpdated: Date | undefined;

  access: UserDetailsViewModel | undefined;
  businessUnitRows: BusinessUnitRow[] = [];

  readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly refreshingFromGraph$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** True when the last load attempt failed; drives the error empty state and retry action. */
  loadFailed: boolean = false;
  /** True after a 409 conflict -- blocks saving until the admin reloads the latest access state. */
  isStale: boolean = false;

  readonly faChevronLeft = faChevronLeft;
  readonly faRotate = faRotate;

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id') ?? '';
    this.loadAccess();
  }

  /** True when at least one non-mandatory (optional) business unit is available to assign. */
  get hasOptionalBusinessUnits(): boolean {
    return this.businessUnitRows.some((row: BusinessUnitRow) => !row.isMandatory);
  }

  /** Combines city, state, and country into a single label, omitting any part missing from the user's Azure profile. */
  get formattedLocation(): string {
    const parts: string[] = [this.access?.city, this.access?.state, this.access?.country]
      .filter((part: string | undefined): part is string => !!part);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  }

  /** Number of business units currently checked, mandatory or optional -- shown as a quick summary next to the section title. */
  get checkedBusinessUnitCount(): number {
    return this.businessUnitRows.filter((row: BusinessUnitRow) => row.isChecked).length;
  }

  /** Toggles an optional business unit when the administrator clicks anywhere on its card. */
  toggleBusinessUnit(row: BusinessUnitRow): void {
    if (row.isMandatory || this.isStale) {
      return;
    }
    row.isChecked = !row.isChecked;
  }

  /** Loads the user's read-only attributes and business-unit access. Does not refresh from Graph. */
  loadAccess(): void {
    this.loadFailed = false;
    this.isStale = false;

    this.esraApiClient.getUserById(this.userId).pipe(
      indicate(this.loading$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (access: UserDetailsViewModel) => this.applyAccess(access),
      error: (err: unknown) => {
        if (err instanceof ApiException && err.status === 404) {
          this.snackBarService.showError(getApiErrorMessage(err, 'This user no longer exists.'));
          this.router.navigate(['/administration/user-management']);
          return;
        }
        this.loadFailed = true;
        this.snackBarService.showError(getApiErrorMessage(err, 'Failed to load user access.'));
        this.insightsService.logException(err as Error);
      },
    });
  }

  /** Refreshes this user's Azure-synchronized attributes on demand, then reloads the access view. */
  refreshFromGraph(): void {
    this.esraApiClient.getUserByIdAndUpdateFromGraph(this.userId).pipe(
      indicate(this.refreshingFromGraph$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.snackBarService.showInfo('User attributes refreshed from Azure AD.');
        this.loadAccess();
      },
      error: (err: unknown) => {
        this.snackBarService.showError(getApiErrorMessage(err, 'Failed to refresh user from Azure AD.'));
        this.insightsService.logException(err as Error);
      },
    });
  }

  /** Persists the currently checked optional business units, sending back the last-seen concurrency token. */
  save(): void {
    if (this.isStale || !this.accessUpdated) {
      return;
    }

    const request: UpdateUserBusinessUnitAccessRequest = new UpdateUserBusinessUnitAccessRequest({
      businessUnitIds: this.businessUnitRows
        .filter((row: BusinessUnitRow) => !row.isMandatory && row.isChecked)
        .map((row: BusinessUnitRow) => row.businessUnitId),
      expectedAccessUpdated: this.accessUpdated,
    });

    this.esraApiClient.updateUserById(this.userId, request).pipe(
      indicate(this.saving$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (access: UserDetailsViewModel) => {
        this.applyAccess(access);
        this.snackBarService.showInfo('User updated.');
      },
      error: (err: unknown) => {
        if (err instanceof ApiException && err.status === 409) {
          this.isStale = true;
          this.snackBarService.showError(
            'This user\'s access was changed by someone else since it was loaded. Reload the latest access state before saving again.'
          );
        } else if (err instanceof ApiException && err.status === 404) {
          this.snackBarService.showError(getApiErrorMessage(err, 'This user no longer exists.'));
          this.router.navigate(['/administration/user-management']);
        } else {
          this.snackBarService.showError(getApiErrorMessage(err, 'Failed to update user business unit access.'));
        }
        this.insightsService.logException(err as Error);
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/administration/user-management']);
  }

  private applyAccess(access: UserDetailsViewModel): void {
    this.access = access;
    this.accessUpdated = access.accessUpdated;
    this.businessUnitRows = (access.businessUnits ?? []).map((businessUnit: UserBusinessUnitAccessViewModel) => ({
      businessUnitId: businessUnit.businessUnitId ?? '',
      name: businessUnit.name ?? '',
      isMandatory: businessUnit.isMandatory ?? false,
      isChecked: businessUnit.isAssigned ?? false,
    }));
  }
}
